import { useState, useEffect } from 'react';

/**
 * Custom hook to sync component state with URL search parameters.
 *
 * @param {Function} parseParams - A function to parse URLSearchParams into a usable object.
 * @param {Function} getPageFromParams - A function to extract the current page number from URLSearchParams.
 * @returns {Object} An object containing the current params, current page, and their corresponding setters.
 */

const useSyncedSearchParams = (parseParams, getPageFromParams) => {
  // Helper function to get and parse URL search parameters
  const getParamsFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return parseParams(urlParams);
  };
  // Initialize state with parsed URL params
  const [params, setParams] = useState(getParamsFromUrl);

  // Initialize current page from URL params
  const [currentPage, setCurrentPage] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return getPageFromParams(urlParams);
  });

  useEffect(() => {
    // Handler for browser navigation (back/forward buttons)
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      setParams(parseParams(urlParams)); // Update params state from URL
      setCurrentPage(getPageFromParams(urlParams)); // Update current page from URL
    };
    // Listen for popstate events (browser navigation)
    window.addEventListener('popstate', handlePopState);
    // Cleanup listener on component unmount
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [parseParams, getPageFromParams]);

  return {
    params, // Parsed query parameters from URL
    setParams, // Setter to update query params
    currentPage, // Current page number extracted from URL
    setCurrentPage, // Setter to update current page
  };
};

export default useSyncedSearchParams;
